import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  HookParams,
  UnionAgreement,
  UnionAgreementByIDParams,
  UnionAgreementListParams,
  UnionAgreementPayload,
} from '@/libs/interfaces'
import { unionAgreementsApi } from '@/services/api'

export function useUnionAgreementByID({ params, enabled }: HookParams<UnionAgreementByIDParams>) {
  return useQuery<UnionAgreement>({
    queryKey: QUERY_KEYS.unionAgreements.id(params.id || ''),
    queryFn: () => unionAgreementsApi.getByID(params.orgID || '', params.id || ''),
    enabled: (enabled ?? true) && !!params.orgID && !!params.id,
    staleTime: STALE_TIME['1Hour'],
  })
}

export function useUnionAgreementInfinite({
  params,
  enabled,
}: HookParams<UnionAgreementListParams>) {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.unionAgreements.infinite(params),
    queryFn: ({ pageParam }) =>
      unionAgreementsApi.listCursor({
        ...params,
        cursor: pageParam,
      }),
    getNextPageParam: (lastPage) => lastPage.pageInfo.next || undefined,
    getPreviousPageParam: (firstPage) => firstPage.pageInfo.prev || undefined,
    enabled: enabled ?? true,
    initialPageParam: undefined as string | undefined,
    staleTime: STALE_TIME['24Hours'], //24 Hours
  })
}

export function useUnionAgreementsCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, payload }: { orgID: string; payload: UnionAgreementPayload }) =>
      unionAgreementsApi.create(orgID, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unionAgreements.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useUnionAgreementsUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      orgID,
      id,
      payload,
    }: {
      orgID: string
      id: string
      payload: Partial<UnionAgreementPayload>
    }) => unionAgreementsApi.update(orgID, id, payload),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unionAgreements.all })
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.unionAgreements.id(id) })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useUnionAgreementsDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({ orgID, id }: { orgID: string; id: string }) =>
      unionAgreementsApi.delete(orgID, id),
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: QUERY_KEYS.unionAgreements.all })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
